import { FC, useState } from "react";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../../api/api";
import { orderApi } from "../../../api/order.api";
import { IOrder, IOrderEditRequest } from "../../../model/IOrder";
import Modal from "../../common/Modal";
import Button from "../../form/Button";
import Input, { TInputEvent } from "../../form/Input";

// --
// -- The statuses to pick from. The dropdown fills the box beside it; the box
// -- is what gets saved, so a status that is not on this list can still be
// -- typed.
// --
// -- Note that "Processed" and "Restored" are NOT in the backend's
// -- OrderStatus enum, yet they are what the storefront writes and what the
// -- unshipped and late reports filter on. They belong here for that reason.
// --
const STATUSES = [
  'Created', 'Processed', 'Restored', 'Shipped', 'PartiallyShipped',
  'Cancelled', 'Returned', 'Refunded',
].map((value) => ({ title: value, value }));

/** Every field is text while it is being typed; parsed on save. */
type TDraft = Record<string, string>;

const FIGURES = [
  { key: 'shipping', label: 'Shipping' },
  { key: 'shippingProtectionFee', label: 'Shipping Protection Fee' },
  { key: 'handling', label: 'Handling' },
  { key: 'taxes', label: 'Taxes' },
  { key: 'totalPrice', label: 'Total Price' },
];

/** Form field -> the order field it is seeded from. */
const ADDRESS: { section: 'shipping' | 'billing', fields: { key: string, label: string, from: keyof IOrder }[] }[] = [
  {
    section: 'shipping',
    fields: [
      { key: 'shippingName', label: 'Name', from: 'siName' },
      { key: 'shippingPhone', label: 'Phone', from: 'siPhone' },
      { key: 'shippingEmail', label: 'Email', from: 'siEmail' },
      { key: 'shippingAddress', label: 'Street Address', from: 'siStreet' },
      { key: 'shippingCity', label: 'City', from: 'siCity' },
      { key: 'shippingState', label: 'State', from: 'siState' },
      { key: 'shippingZip', label: 'ZIP', from: 'siZip' },
    ],
  },
  {
    section: 'billing',
    fields: [
      { key: 'billingName', label: 'Name', from: 'piName' },
      { key: 'billingPhone', label: 'Phone', from: 'piPhone' },
      { key: 'billingEmail', label: 'Email', from: 'piEmail' },
      { key: 'billingAddress', label: 'Street Address', from: 'piStreet' },
      { key: 'billingCity', label: 'City', from: 'piCity' },
      { key: 'billingState', label: 'State', from: 'piState' },
      { key: 'billingZip', label: 'ZIP', from: 'piZip' },
    ],
  },
];

const text = (value: unknown) => (value === null || value === undefined ? '' : String(value));

const asDraft = (order: IOrder): TDraft => {
  const draft: TDraft = {
    status: order.status || '',
    notes: order.notes || '',
  };
  FIGURES.forEach(({ key }) => { draft[key] = text((order as never)[key]); });
  ADDRESS.forEach(({ fields }) => fields.forEach(({ key, from }) => { draft[key] = text(order[from]); }));
  return draft;
};

/** Blank clears the amount; anything else has to be a number. */
const parseAmount = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
};

const Section: FC<{ title: string }> = ({ title }) => (
  <div className="col-span-12 border-b-[1px] border-border_color pb-1 mt-2 font-semibold">{title}</div>
);

/**
 * Editing an order, for an admin or an agent.
 *
 * One form rather than three, because the figures and the two addresses are
 * usually corrected together - an order fixed up after a phone call changes
 * the address and the total in the same breath.
 *
 * The totals are not reconciled against the line items, deliberately: an
 * agent correcting an order often knows the right total before the lines
 * agree with it.
 */
const OrderEditModal: FC<{ order: IOrder, onClose: () => void }> = ({ order, onClose }) => {
  const [draft, setDraft] = useState<TDraft>(() => asDraft(order));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [save, { isLoading }] = orderApi.useUpdateOrderDetailsMutation();

  const set = (key: string) => (e: TInputEvent) =>
    setDraft((prev) => ({ ...prev, [key]: e?.target?.value ?? '' }));

  const handleSave = async () => {
    const next: Record<string, string> = {};
    const request: IOrderEditRequest = {
      status: draft.status || undefined,
      notes: draft.notes,
    };

    FIGURES.forEach(({ key, label }) => {
      const value = parseAmount(draft[key]);
      if (value === undefined) next[key] = `${label} must be a number`;
      else (request as never as Record<string, unknown>)[key] = value;
    });

    ADDRESS.forEach(({ fields }) => fields.forEach(({ key }) => {
      (request as never as Record<string, unknown>)[key] = draft[key];
    }));

    setErrors(next);
    if (Object.keys(next).length) return;

    try {
      await save({ orderId: order.orderId, ...request }).unwrap();
      toast.success('Order updated');
      onClose();
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Could not save the order');
    }
  };

  const field = (key: string, label: string, className = 'col-span-6 xl:col-span-3') => (
    <Input
      key={key}
      label={label}
      name={key}
      value={draft[key]}
      handle={set(key)}
      error={errors[key]}
      isError={!!errors[key]}
      disabled={isLoading}
      className={className}
    />
  );

  return (
    <Modal
      isShown
      title={`Edit Order ${order.orderId}`}
      style={{ maxWidth: '980px' }}
      onClose={onClose}
    >
      <div className="w-full grid grid-cols-12 gap-x-5 gap-y-4">
        {/*
          Two controls over one value. The dropdown is a shortcut that writes
          into the box; the box is the value that is saved, so a status the
          list does not carry can still be entered.
        */}
        <Input
          label="Status"
          name="statusPreset"
          value={STATUSES.some(({ value }) => value === draft.status) ? draft.status : ''}
          options={STATUSES}
          handle={set('status')}
          disabled={isLoading}
          className="col-span-6 xl:col-span-3"
        />
        <Input
          label="Status (or type one)"
          name="status"
          placeholder="e.g. Processed"
          value={draft.status}
          handle={set('status')}
          disabled={isLoading}
          className="col-span-6 xl:col-span-3"
        />
        <Input
          label="Comments"
          name="notes"
          type="textarea"
          value={draft.notes}
          handle={set('notes')}
          disabled={isLoading}
          className="col-span-12 xl:col-span-6"
        />

        <Section title="Figures" />
        {FIGURES.map(({ key, label }) => field(key, label))}

        <Section title="Shipping Address" />
        {ADDRESS[0].fields.map(({ key, label }) => field(key, label))}

        <Section title="Billing Address" />
        {ADDRESS[1].fields.map(({ key, label }) => field(key, label))}

        <p className="col-span-12 text-text_pale text-sm">
          Totals are saved as entered and are not checked against the line items.
          Changing the status adds an entry to the order's history.
        </p>

        <div className="col-span-12 flex justify-end gap-3">
          <Button isNotFullWidth disabled={isLoading} onClick={onClose}>Cancel</Button>
          <Button isNotFullWidth type="submit" disabled={isLoading} onClick={handleSave}>
            {isLoading ? 'Saving…' : 'Save Order'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default OrderEditModal;
